// Pure project helpers shared by the client-summary API (cards + MCP) AND the Clients page
// (src/clients imports this file directly), so the "which project is primary" and "which
// project does this objective/task belong to" rules can never drift between them. No I/O.

const isArchived = (p) => !!p.archivedAt;

export const projectTitle = (p) =>
  p.projectType === "proposal-plan" ? "6-Month Proposal Plan" : (p.customTitle || p.projectType || "Project");

// Non-archived projects, earliest start first.
export function liveProjectsOf(projects) {
  return (projects || []).filter((p) => !isArchived(p))
    .sort((a, b) => ((a.startDate || "") < (b.startDate || "") ? -1 : (a.startDate || "") > (b.startDate || "") ? 1 : 0));
}

// The client's primary project: the one active today (latest start if several), else the
// soonest upcoming, else the most recently ended. Missing endDate = open-ended. Archived
// projects never count. `today` is "YYYY-MM-DD".
export function primaryProjectOf(projects, today) {
  const live = liveProjectsOf(projects);
  if (!live.length) return null;
  const start = (p) => p.startDate || "";
  const active = live.filter((p) => start(p) <= today && (!p.endDate || p.endDate >= today));
  if (active.length) return active[active.length - 1];
  const upcoming = live.filter((p) => start(p) > today);
  if (upcoming.length) return upcoming[0];
  return [...live].sort((a, b) => ((a.endDate || "") < (b.endDate || "") ? 1 : -1))[0];
}

// Which live project an objective/task belongs to, as a function (id → projectId | null).
// Own projectId if it names a live project; a task without one inherits its objective's;
// unlinked (or pointing at an unknown project) → the primary project; linked to an ARCHIVED
// project → null (that work belongs to no live project).
export function projectAttribution(projects, objectives, today) {
  const all = new Map((projects || []).map((p) => [p.id, p]));
  const primaryId = primaryProjectOf(projects, today)?.id || null;
  const resolve = (projectId) => {
    const p = projectId ? all.get(projectId) : null;
    if (!p) return primaryId;
    return isArchived(p) ? null : p.id;
  };
  const objProject = new Map((objectives || []).map((o) => [o.id, o.projectId || null]));
  return {
    primaryId,
    ofObjective: (o) => resolve(o.projectId),
    ofTask: (t) => resolve(t.projectId || (t.objectiveId ? objProject.get(t.objectiveId) : null) || null),
  };
}
